"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";

import { PageTwitterSchema } from "@/sanity/pages/PageTwitterTemplate";

import CheckMarkGreenIcon from "@/public/login/check-mark-green.svg";
import logoImg from "@/public/trading-guide/logo.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function TextSection({
  data,
  bitunix = false,
  phemex = false,
}: {
  data: PageTwitterSchema;
  bitunix?: boolean;
  phemex?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const dearTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      dearTL.from(content1Ref.current.children, {
        y: "1rem",
        stagger: 0.1,
        delay: 0.25,
        scrollTrigger: {
          trigger: content1Ref.current,
          end: "center center",
          scrub: 1,
        },
      });
    },
    { scope: containerRef }
  );

  return (
    <section ref={containerRef} className="w-full">
      <div className="container-lp mx-auto pt-[20rem] lg:pt-[30rem] lg:pb-[20rem]">
        <div className="flex flex-col items-start justify-center w-full max-w-[104rem] mx-auto">
          <div ref={content1Ref}>
            <div className="mb-[2rem] w-full relative">
              <Image
                src={logoImg}
                alt="Logo"
                className="mb-[2rem] max-w-[8rem]"
              />

              {data?.textTitles && (
                <PortableText
                  value={data?.textTitles ?? []}
                  components={customRichTextComponent({
                    block: {
                      h3: ({ children }) => (
                        <h3 className="gradient-title text-[5rem] lg:text-[6rem] font-[500] tracking-[-0.35rem] lg:tracking-[-0.49rem] leading-[110%] lg:leading-[129%]">
                          {children}
                        </h3>
                      ),
                      h4: ({ children }) => (
                        <h4 className="text-white/80 text-[3.5rem] font-[500] leading-[140%] mb-[3rem]">
                          {children}
                        </h4>
                      ),
                      normal: ({ children }) => (
                        <p className="text-[#BFBFBF] text-[2.5rem] font-[400] leading-[150%] mb-0">
                          {children}
                        </p>
                      ),
                    },
                    marks: {
                      orangeFlare: ({ children }) => (
                        <span
                          className={
                            bitunix
                              ? "bitunix-shadow"
                              : phemex
                                ? "phemex-shadow"
                                : "orange-shadow"
                          }
                        >
                          {children}
                        </span>
                      ),
                    },
                    listItem: {
                      bullet: ({ children }) => (
                        <li className="text-[#BFBFBF] text-[2.5rem] font-[400] leading-[150%] w-full flex items-center gap-2">
                          <Image
                            className="w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem]"
                            src={CheckMarkGreenIcon}
                            alt="check"
                          />
                          <span>{children}</span>
                        </li>
                      ),
                    },
                  })}
                />
              )}

            </div>

          </div>

          <div className="w-full flex justify-start items-center mt-[3rem] lg:mt-[4rem]">
            {data?.textCTA && <CTAButton data={data?.textCTA} />}
          </div>
        </div>
      </div>
    </section>
  );
}
